import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_FILTERS } from '@/types/Filters';
import type { Filters, Period } from '@/types/Filters';
import type { MoneyView } from '@/types/Transaction';

const STORAGE_KEY = 'aiobi-filters';

export type FilterAction =
  | { type: 'SET_PERIOD'; payload: Period }
  | { type: 'SET_SUPPLIERS'; payload: string[] }
  | { type: 'SET_CATEGORIES'; payload: string[] }
  | { type: 'SET_MONEY_VIEW'; payload: MoneyView }
  | { type: 'RESET' };

// eslint-disable-next-line react-refresh/only-export-components
export function filtersReducer(state: Filters, action: FilterAction): Filters {
  switch (action.type) {
    case 'SET_PERIOD':
      return { ...state, period: action.payload };
    case 'SET_SUPPLIERS':
      return { ...state, suppliers: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_MONEY_VIEW':
      return { ...state, moneyView: action.payload };
    case 'RESET':
      return DEFAULT_FILTERS;
  }
}

interface SerializedPeriod {
  preset: Period['preset'];
  customRange?: { from: string; to: string };
}

function serialize(f: Filters): string {
  const period: SerializedPeriod = {
    preset: f.period.preset,
    ...(f.period.customRange && {
      customRange: {
        from: f.period.customRange.from.toISOString(),
        to: f.period.customRange.to.toISOString(),
      },
    }),
  };
  return JSON.stringify({ ...f, period });
}

function deserialize(json: string): Filters | null {
  try {
    const obj = JSON.parse(json) as Partial<Filters> & { period?: SerializedPeriod };
    if (!obj.period || !obj.suppliers || !obj.categories || !obj.moneyView || !obj.locale) {
      return null;
    }
    const period: Period = { preset: obj.period.preset };
    if (obj.period.customRange) {
      period.customRange = {
        from: new Date(obj.period.customRange.from),
        to: new Date(obj.period.customRange.to),
      };
    }
    return {
      period,
      suppliers: obj.suppliers,
      categories: obj.categories,
      moneyView: obj.moneyView,
      locale: obj.locale,
    };
  } catch {
    return null;
  }
}

function readInitial(): Filters {
  if (typeof window === 'undefined') return DEFAULT_FILTERS;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return DEFAULT_FILTERS;
  return deserialize(raw) ?? DEFAULT_FILTERS;
}

/** Conta filtros activos vs default (util para badge no ResetButton). */
// eslint-disable-next-line react-refresh/only-export-components
export function activeFiltersCount(f: Filters): number {
  let n = 0;
  if (f.period.preset !== DEFAULT_FILTERS.period.preset) n++;
  if (f.suppliers.length > 0) n++;
  if (f.categories.length > 0) n++;
  if (f.moneyView !== DEFAULT_FILTERS.moneyView) n++;
  return n;
}

interface FilterContextValue {
  filters: Filters;
  dispatch: React.Dispatch<FilterAction>;
  activeCount: number;
}

const FilterContext = createContext<FilterContextValue | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [filters, dispatch] = useReducer(filtersReducer, undefined, readInitial);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, serialize(filters));
    } catch {
      // QuotaExceeded ou private mode — silencioso, filtros funcionam in-memory
    }
  }, [filters]);

  const value = useMemo<FilterContextValue>(
    () => ({ filters, dispatch, activeCount: activeFiltersCount(filters) }),
    [filters],
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFilters(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters deve ser usado dentro de <FilterProvider>');
  return ctx;
}
